import './onboarding.scss';

import { Button, RingLoader } from '@basqet-dashboard/common/components';
import { useUserContext } from '@basqet-dashboard/common/context';
import { FLAGS } from '@basqet-dashboard/common/utils';
import { ANALYTICS_EVENTS } from '@config';
import { API_TO_ACCOUNT_TYPE } from '@config/account-types';
import { useAnalytics, useTrackOnMount } from '@hooks';
import { useCompliance } from '@hooks/useCompliance';
import classNames from 'classnames';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';

import {
  ACCOUNT_TYPES,
  MODAL_CONTENT
} from '../../components/account-type-selector/account-type-selector';

const MERCHANT_TYPE_TO_ROUTE = {
  FREELANCER: '/onboard/freelancer',
  CORP_NON_FI: '/onboard/corporate_non_fi',
  CORP_FI: '/onboard/corporate_fi'
};

const Onboarding = () => {
  const onboardingEnabled = useFeatureFlagEnabled(FLAGS.ONBOARDING_V2);
  const { track } = useAnalytics();
  const history = useHistory();
  const { user } = useUserContext();
  const defaultType = API_TO_ACCOUNT_TYPE[user?.accountType] || ACCOUNT_TYPES[0].value;
  const [selectedType, setSelectedType] = useState(defaultType);
  const [checking, setChecking] = useState(true);
  const { loadProfile } = useCompliance();

  useEffect(() => {
    const mapped = API_TO_ACCOUNT_TYPE[user?.accountType];
    if (mapped) setSelectedType(mapped);
  }, [user?.accountType]);

  useTrackOnMount(ANALYTICS_EVENTS.KYB_OVERVIEW_VIEWED);

  useEffect(() => {
    const checkExistingKyc = async () => {
      try {
        const profile = await loadProfile();
        const route = profile ? MERCHANT_TYPE_TO_ROUTE[profile.merchant_type] : null;
        if (route) {
          history.replace(route);
          return;
        }
      } catch {
        // If profile check fails, fall through to show the selector
      } finally {
        setChecking(false);
      }
    };

    checkExistingKyc();
  }, [loadProfile, history]);

  if (onboardingEnabled === false || user?.isKycVerified) {
    return <Redirect to="/" />;
  }

  if (checking) {
    return (
      <div className="onboarding">
        <div className="onboarding__card onboarding__card--loading">
          <RingLoader />
        </div>
      </div>
    );
  }

  const content = MODAL_CONTENT[selectedType];

  return (
    <div className="onboarding">
      <div className="onboarding__card">
        <div className="onboarding__header">
          <h2 className="onboarding__title">
            Your Business Profile <span className="text-cursive">Kickoff</span>
          </h2>
          <p className="onboarding__subtitle">
            We use your account type and industry to tailor verification steps and ensure
            compliance. If you need to adjust your account type, you can do so here.
          </p>
        </div>

        <hr className="onboarding__divider" />

        <div className="onboarding__type-section">
          <h3 className="onboarding__section-title">Select Account Type</h3>
          <p className="onboarding__section-subtitle">
            This should match how your business is registered and how you will receive payments.
          </p>

          <div className="onboarding__tabs">
            {ACCOUNT_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                className={classNames('onboarding__tab', {
                  'onboarding__tab--selected': selectedType === type.value
                })}
                onClick={() => setSelectedType(type.value)}>
                <span className="onboarding__tab-radio">
                  {selectedType === type.value && <span className="onboarding__tab-radio-dot" />}
                </span>
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {content && (
          <div className="onboarding__info">
            <p className="onboarding__info-description">{content.description}</p>
            <div className="onboarding__info-inner">
              <div className="onboarding__info-section">
                <h4 className="onboarding__info-heading">Why we collect this information</h4>
                <p className="onboarding__info-text">{content.why}</p>
              </div>
              <div className="onboarding__info-section">
                <h4 className="onboarding__info-heading">What we&apos;ll need from you</h4>
                <ul className="onboarding__info-list">
                  {content.needs.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <Button
          isFull
          onClick={() => {
            track(ANALYTICS_EVENTS.KYB_START_CLICKED, {
              kyb_account_type: selectedType
            });
            history.push(`/onboard/${selectedType}`);
          }}>
          Start Verification
        </Button>
      </div>
    </div>
  );
};

export { Onboarding };
